import React, { useRef, useState, useEffect } from 'react';
import CircleTimer from './CircleTimer';
import UnresponsiveKickList from './UnresponsiveKickList';
import './PromptEntry.css';

// Playful, drawable phrases to nudge players. Kept in sync with iOS PromptEntryView.swift.
const PLACEHOLDER_SUGGESTIONS = [
  'a sleepy dragon making toast',
  'a cat riding a unicycle',
  'two robots sharing an umbrella',
  'a penguin at the beach',
  'a wizard losing a sock',
  'a banana driving a tractor',
  'a shark in a bubble bath',
  'an octopus playing drums',
  'a snail winning a marathon',
  'a ghost ordering coffee',
  'a dinosaur watering plants',
  'a frog in a tiny hat',
  'a llama doing yoga',
  'a pirate baking cupcakes',
  'an alien walking a dog',
];

function PromptEntry({ roomCode, playerName, players, playerId, submittedCount, onSubmitPrompt, timeLeft: initialTimeLeft, alreadySubmitted, submittedPrompt, promptSuggestions, isHost, onKickPlayer, submissionError, onClearSubmissionError }) {
  const [prompt, setPrompt] = useState(submittedPrompt || '');
  const [hasSubmitted, setHasSubmitted] = useState(alreadySubmitted || false);
  const [isEditing, setIsEditing] = useState(false);
  const noLimit = (initialTimeLeft === 0);
  const [timeLeft, setTimeLeft] = useState(noLimit ? 0 : (initialTimeLeft || 60));
  const maxTimeRef = useRef(noLimit ? 0 : (initialTimeLeft || 60));

  const [placeholder] = useState(() => PLACEHOLDER_SUGGESTIONS[Math.floor(Math.random() * PLACEHOLDER_SUGGESTIONS.length)]);

  const canEdit = (!hasSubmitted || isEditing) && (noLimit || timeLeft > 0);

  // Effect 1: Pure countdown (skip if no-limit)
  useEffect(() => {
    if (noLimit) return;
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 2: Auto-submit when timer reaches 0 (skip if no-limit)
  useEffect(() => {
    if (noLimit) return;
    if (timeLeft !== 0) return;
    if (!prompt.trim()) return;
    onSubmitPrompt(prompt.trim());
    setHasSubmitted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  // The server rejected this prompt (content moderation). Re-open the field
  // so the player can reword and resubmit instead of being stuck "submitted".
  useEffect(() => {
    if (submissionError) {
      setHasSubmitted(false);
      setIsEditing(true);
    }
  }, [submissionError]);

  const handleSubmit = () => {
    if (prompt.trim()) {
      onSubmitPrompt(prompt.trim());
      setHasSubmitted(true);
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const suggestions = promptSuggestions || [];

  const waitingPlayers = players.filter(p => !p.submitted);
  const waitingText =
    waitingPlayers.length === 0 ? 'Almost done!' :
    waitingPlayers.length === 1 ? `Waiting for ${waitingPlayers[0].name}...` :
    waitingPlayers.length === 2 ? `Waiting for ${waitingPlayers[0].name} and ${waitingPlayers[1].name}...` :
    `Waiting for ${waitingPlayers.length} more...`;

  return (
    <div className="prompt-container">
      <div className="prompt-card">
        <div className="prompt-header">
          <div className="prompt-header-text">
            <h1 className="prompt-title"><span className="material-symbols-outlined" style={{fontSize: 'inherit', verticalAlign: 'middle', marginRight: '8px'}}>edit_note</span>Write a Prompt</h1>
            <p className="prompt-subtitle">
              Enter a word or phrase for someone else to draw!
            </p>
          </div>
          <div className="prompt-header-right">
            <CircleTimer timeLeft={timeLeft} maxTime={maxTimeRef.current} />
            {canEdit ? (
              <button className="submit-prompt-button-top" onClick={handleSubmit} disabled={!prompt.trim()}>Done</button>
            ) : hasSubmitted && (noLimit || timeLeft > 0) ? (
              <button className="edit-prompt-button-top" onClick={() => setIsEditing(true)}>Edit</button>
            ) : hasSubmitted ? (
              <span className="prompt-submitted-badge"><span className="material-symbols-outlined" style={{fontSize:'12px'}}>check_circle</span> Submitted</span>
            ) : null}
          </div>
        </div>

        <div className="prompt-form">
          <input
            type="text"
            className={`prompt-input${!canEdit ? ' prompt-input--locked' : ''}`}
            placeholder={`e.g. ${placeholder}`}
            value={prompt}
            onChange={(e) => {
              if (!canEdit) return;
              setPrompt(e.target.value);
              if (submissionError) onClearSubmissionError?.();
            }}
            onKeyDown={handleKeyDown}
            maxLength={100}
            autoComplete="off"
            disabled={!canEdit}
            autoFocus={canEdit}
          />
          {submissionError && (
            <div className="prompt-moderation-error" role="alert">
              <span className="material-symbols-outlined" style={{fontSize:'15px'}}>error</span>
              {submissionError}
            </div>
          )}
          {canEdit && <div className="char-count">{prompt.length}/100</div>}
          {!canEdit && (
            <div className="submission-status">
              <span className="material-symbols-outlined" style={{fontSize:'13px'}}>
                {hasSubmitted ? 'check_circle' : 'timer'}
              </span>
              <span>{hasSubmitted ? ' Submitted' : " Time's up"}</span>
              <span className="submission-waiting"> · {waitingText}</span>
            </div>
          )}
          {canEdit && suggestions.length > 0 && (
            <div className="prompt-suggestions">
              <p className="prompt-suggestions-label">Suggestions:</p>
              <div className="prompt-suggestions-chips">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    className="prompt-suggestion-chip"
                    onClick={() => setPrompt(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          <UnresponsiveKickList players={players} isHost={isHost} onKickPlayer={onKickPlayer} />
        </div>

      </div>
    </div>
  );
}

export default PromptEntry;
